/**
 * A tiny typed HTTP client on top of fetch (ACA-104).
 *
 * It replaced axios, which was a tenth of the bundle for the two verbs the
 * widget uses. The shapes deliberately match what axios gave the rest of the
 * code, so callers and tests read the same:
 * - a request resolves for 2xx and rejects with an `HttpError` otherwise,
 *   carrying `response.status` and the parsed `response.data`;
 * - a failure with no answer at all, or an answer whose body never fully
 *   arrives, rejects with `Network Error` and no `response`;
 * - a body that is not JSON comes back as the raw text;
 * - the adapter is swappable (`httpDefaults.adapter`), and a client copies it
 *   when it is created, so tests and the dev mock install theirs first.
 */

export type HttpMethod = 'get' | 'post'

export type HttpParams = URLSearchParams | Record<string, string>

export interface HttpRequestConfig {
  method: HttpMethod
  baseURL: string
  /** The path as the caller gave it, before the base URL is joined on. */
  url: string
  params?: HttpParams
  /** The body, already serialised to JSON. */
  data?: string
  headers: Record<string, string>
  withCredentials: boolean
}

/** The body is `any` unless the caller says, as it was with axios. */
export interface HttpResponse<T = any> {
  data: T
  status: number
  statusText: string
  headers: Record<string, string>
  config: HttpRequestConfig
}

export type HttpAdapter = (config: HttpRequestConfig) => Promise<HttpResponse>

export class HttpError<T = unknown> extends Error {
  /** A brand rather than instanceof, so an error from another copy of this module still counts. */
  readonly isHttpError = true
  readonly code?: string
  readonly config?: HttpRequestConfig
  readonly request?: unknown
  readonly response?: HttpResponse<T>

  constructor(
    message: string,
    code?: string,
    config?: HttpRequestConfig,
    request?: unknown,
    response?: HttpResponse<T>
  ) {
    super(message)
    this.name = 'HttpError'
    this.code = code
    this.config = config
    this.request = request
    this.response = response
  }
}

export function isHttpError(error: unknown): error is HttpError {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { isHttpError?: unknown }).isHttpError === true
  )
}

export interface HttpRequestOptions {
  params?: HttpParams
  headers?: Record<string, string>
}

export interface HttpClient {
  get<T = any>(url: string, options?: HttpRequestOptions): Promise<HttpResponse<T>>
  post<T = any>(url: string, body?: unknown, options?: HttpRequestOptions): Promise<HttpResponse<T>>
}

export interface HttpClientOptions {
  baseURL: string
  withCredentials?: boolean
}

const ACCEPT = 'application/json, text/plain, */*'

function joinUrl(baseURL: string, url: string): string {
  if (/^([a-z][a-z\d+\-.]*:)?\/\//i.test(url) || !baseURL) return url
  return `${baseURL.replace(/\/?\/$/, '')}/${url.replace(/^\/+/, '')}`
}

function withParams(url: string, params: HttpParams | undefined): string {
  if (!params) return url
  const query = (params instanceof URLSearchParams ? params : new URLSearchParams(params)).toString()
  if (!query) return url
  const bare = url.split('#')[0]
  return `${bare}${bare.includes('?') ? '&' : '?'}${query}`
}

function parseBody(text: string): unknown {
  if (!text) return text
  try {
    return JSON.parse(text) as unknown
  } catch {
    return text
  }
}

async function fetchAdapter(config: HttpRequestConfig): Promise<HttpResponse> {
  let res: Response
  try {
    res = await fetch(withParams(joinUrl(config.baseURL, config.url), config.params), {
      method: config.method.toUpperCase(),
      headers: config.headers,
      body: config.data,
      credentials: config.withCredentials ? 'include' : 'same-origin',
    })
  } catch {
    throw new HttpError('Network Error', 'ERR_NETWORK', config)
  }

  // The connection can still drop while the body streams in: that is a
  // network failure too, never a raw TypeError reaching the caller.
  let text: string
  try {
    text = await res.text()
  } catch {
    throw new HttpError('Network Error', 'ERR_NETWORK', config)
  }

  const headers: Record<string, string> = {}
  res.headers.forEach((value, key) => {
    headers[key] = value
  })

  const response: HttpResponse = {
    data: parseBody(text),
    status: res.status,
    statusText: res.statusText,
    headers,
    config,
  }
  if (res.status >= 200 && res.status < 300) return response

  throw new HttpError(
    `Request failed with status code ${res.status}`,
    res.status >= 500 ? 'ERR_BAD_RESPONSE' : 'ERR_BAD_REQUEST',
    config,
    null,
    response
  )
}

export const httpDefaults: { adapter: HttpAdapter } = { adapter: fetchAdapter }

export function createHttpClient(options: HttpClientOptions): HttpClient {
  const adapter = httpDefaults.adapter
  const withCredentials = options.withCredentials ?? false

  async function request<T>(
    method: HttpMethod,
    url: string,
    body: unknown,
    requestOptions: HttpRequestOptions | undefined
  ): Promise<HttpResponse<T>> {
    const hasBody = body !== undefined
    const config: HttpRequestConfig = {
      method,
      baseURL: options.baseURL,
      url,
      params: requestOptions?.params,
      data: hasBody ? JSON.stringify(body) : undefined,
      headers: {
        Accept: ACCEPT,
        ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
        ...requestOptions?.headers,
      },
      withCredentials,
    }
    return (await adapter(config)) as HttpResponse<T>
  }

  return {
    get: <T>(url: string, requestOptions?: HttpRequestOptions) =>
      request<T>('get', url, undefined, requestOptions),
    post: <T>(url: string, body?: unknown, requestOptions?: HttpRequestOptions) =>
      request<T>('post', url, body, requestOptions),
  }
}
